import React, { useState, useEffect, useMemo } from 'react';
import { jsPDF } from 'jspdf';
import API from '../services/api';
import { todayEastern } from '../dateUtils';

// Helper function to safely get signature data regardless of field names
function getSignatureData(form, type) {
  if (!form) return '';
  
  let sigData = '';
  
  if (type === 'consent') {
    sigData = form.consent_signature || 
              form.consentSignatureData || 
              (form.data_json && form.data_json.consentSignatureData) || 
              form.signature || 
              '';
  } else if (type === 'cancel') {
    sigData = form.cancel_signature || 
              form.cancelSignatureData || 
              (form.data_json && form.data_json.cancelSignatureData) || 
              form.signature || 
              '';
  } else if (type === 'final') {
    sigData = form.final_signature || 
              form.finalSignatureData || 
              (form.data_json && form.data_json.finalSignatureData) || 
              form.signature || 
              '';
  }
  
  return sigData;
}

// Utility functions for formatting
function formatSessionDate(rawDate) {
  if (!rawDate) return '';
  const datePart = String(rawDate).split('T')[0];
  const [y, m, d] = datePart.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString();
}

function formatCurrency(amount) {
  return `$${parseFloat(amount || 0).toFixed(2)}`;
}

function getPaymentStyle(paymentType) {
  const lower = (paymentType || '').toLowerCase();
  let bg = 'lightgray';
  let fg = '#000';
  if (lower.includes('cash')) {
    bg = 'green';
    fg = '#fff';
  } else if (lower.includes('card')) {
    bg = 'blue';
    fg = '#fff';
  } else if (lower.includes('check')) {
    bg = 'yellow';
    fg = '#000';
  }
  return {
    backgroundColor: bg,
    color: fg,
    padding: '2px 6px',
    borderRadius: '4px',
    display: 'inline-block',
  };
}

function getStatusBadge(status) {
  const statusColors = {
    'PAID': { bg: '#28a745', color: 'white' },
    'UNPAID': { bg: '#dc3545', color: 'white' },
    'DEBT': { bg: '#fd7e14', color: 'white' },
    'SUBMITTED': { bg: '#6f42c1', color: 'white' }
  };
  
  const style = statusColors[status] || { bg: '#6c757d', color: 'white' };
  
  return {
    backgroundColor: style.bg,
    color: style.color,
    padding: '2px 8px',
    borderRadius: '12px',
    fontSize: '0.85rem',
    fontWeight: 'bold',
    display: 'inline-block'
  };
}

function AdminClientProfilePage() {
  // Client management
  const [allClients, setAllClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [isEditingClient, setIsEditingClient] = useState(false);
  const [editedClientData, setEditedClientData] = useState({});

  // Billing history
  const [billingHistory, setBillingHistory] = useState([]);
  const [billingFilters, setBillingFilters] = useState({
    startDate: '',
    endDate: '',
    status: 'ALL',
    searchTerm: ''
  });
  const [billingPage, setBillingPage] = useState(0);
  const [hasBillingMore, setHasBillingMore] = useState(false);

  // Forms management
  const [localForms, setLocalForms] = useState([]);
  const [selectedForm, setSelectedForm] = useState(null);
  const [showFormPreview, setShowFormPreview] = useState(false);

  // Statistics
  const [clientStats, setClientStats] = useState({});
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [diagnosticData, setDiagnosticData] = useState(null);
  const [showDiagnostic, setShowDiagnostic] = useState(false);

  // Modal states
  const [showClientEditor, setShowClientEditor] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);

  // Active tab
  const [activeTab, setActiveTab] = useState('overview');

  // Store therapist info separately since it's not in the initial client list
  const [clientTherapists, setClientTherapists] = useState({});

  // Helper function to get therapist name - now uses backend data first
  const getClientTherapist = (client) => {
    if (!client) {
      console.log('getClientTherapist: No client provided');
      return null;
    }
    
    console.log('getClientTherapist called for:', client.client_name);
    console.log('Client object keys:', Object.keys(client));
    console.log('therapist_first_name:', client.therapist_first_name);
    console.log('therapist_last_name:', client.therapist_last_name);
    
    // First check if we have it from the backend (new approach)
    if (client.therapist_first_name && client.therapist_last_name) {
      const therapistName = `${client.therapist_first_name} ${client.therapist_last_name}`;
      console.log('✅ Found therapist from backend:', therapistName);
      return therapistName;
    }
    
    // Fallback to stored data from billing history
    if (clientTherapists[client.id]) {
      console.log('✅ Found therapist from stored data:', clientTherapists[client.id]);
      return clientTherapists[client.id];
    }
    
    // Final fallback to billing history lookup
    if (billingHistory.length > 0) {
      const clientSessions = billingHistory.filter(session => 
        session.client_name === client.client_name
      );
      
      if (clientSessions.length > 0) {
        const sortedSessions = clientSessions.sort((a, b) => 
          new Date(b.session_date) - new Date(a.session_date)
        );
        
        const mostRecentSession = sortedSessions[0];
        if (mostRecentSession?.therapist_first_name && mostRecentSession?.therapist_last_name) {
          const therapistName = `${mostRecentSession.therapist_first_name} ${mostRecentSession.therapist_last_name}`;
          console.log('✅ Found therapist from billing history:', therapistName);
          
          // Store it for future use
          setClientTherapists(prev => ({
            ...prev,
            [client.id]: therapistName
          }));
          
          return therapistName;
        }
      }
    }
    
    console.log('❌ No therapist found for client:', client.client_name);
    return null;
  };

  // Use the original allClients since they don't include therapist info
  const clientsWithTherapists = allClients;

  useEffect(() => {
    fetchAllPortalClients();
  }, []);

  useEffect(() => {
    if (selectedClient) {
      // Reset states first
      setBillingHistory([]);
      setLocalForms([]);
      setClientStats({});
      setBillingPage(0);
      
      // Then fetch data
      fetchClientBillingHistory();
      fetchClientForms();
      fetchClientStats();
    }
  }, [selectedClient]); // Remove billingFilters from dependency array

  // Extract therapist info when billing history is loaded
  useEffect(() => {
    if (selectedClient && billingHistory.length > 0) {
      const therapistName = getClientTherapist(selectedClient.id, selectedClient.client_name);
      if (therapistName && !clientTherapists[selectedClient.id]) {
        setClientTherapists(prev => ({
          ...prev,
          [selectedClient.id]: therapistName
        }));
      }
    }
  }, [billingHistory, selectedClient]);

  // Separate useEffect for billing filters
  useEffect(() => {
    if (selectedClient && (billingFilters.startDate || billingFilters.endDate || billingFilters.status !== 'ALL')) {
      fetchClientBillingHistory();
    }
  }, [billingFilters]);

  // Client management functions
  const fetchAllPortalClients = async () => {
    try {
      const res = await API.get('/admin/clients');
      console.log('=== CLIENT DATA DEBUG ===');
      console.log('Raw API response:', res.data);
      if (res.data && res.data.length > 0) {
        console.log('First client:', res.data[0]);
        console.log('Has therapist_first_name?', !!res.data[0].therapist_first_name);
        console.log('Has therapist_last_name?', !!res.data[0].therapist_last_name);
      }
      console.log('=== END DEBUG ===');
      setAllClients(res.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  // Remove the fetchClientTherapistInfo function since we don't need it anymore
  const handleSelectClient = async (client) => {
    setSelectedClient(client);
    setActiveTab('overview');
    setBillingPage(0);
    
    // Reset editing state
    setIsEditingClient(false);
    setEditedClientData({});
    
    // Reset stats
    setClientStats({});
  };

  const startEditingClient = () => {
    setEditedClientData({
      clientName: selectedClient.client_name || '',
      phone: selectedClient.phone || '',
      email: selectedClient.email || '',
      emergencyContactName: selectedClient.emergency_contact_name || '',
      emergencyContactPhone: selectedClient.emergency_contact_phone || '',
      isMinor: selectedClient.is_minor || false,
      guardianName: selectedClient.guardian_name || '',
      guardianPhone: selectedClient.guardian_phone || '',
      diagnosisCode: selectedClient.diagnosis_code || '',
      billingEmail: selectedClient.billing_email || '',
      billingAddress: selectedClient.billing_address || ''
    });
    setIsEditingClient(true);
    setShowClientEditor(true);
  };

  const saveClientEdits = async () => {
    try {
      await API.put(`/admin/clients/${selectedClient.id}`, editedClientData);
      
      // Refresh client data
      const updatedClient = await API.get(`/admin/clients/${selectedClient.id}`);
      setSelectedClient(updatedClient.data);
      
      // Update clients list
      setAllClients(prev => prev.map(c => 
        c.id === selectedClient.id 
          ? { ...c, client_name: editedClientData.clientName }
          : c
      ));
      
      setIsEditingClient(false);
      setShowClientEditor(false);
      alert('Client updated successfully!');
    } catch (error) {
      console.error('Error updating client:', error);
      alert('Error updating client information');
    }
  };

  // Billing history functions
  const fetchClientBillingHistory = async (resetPage = true) => {
    if (!selectedClient) return;
    
    try {
      let pageToUse = resetPage ? 0 : billingPage;
      if (resetPage) setBillingPage(0);

      let url = `/admin/daily-report/history?limit=21&offset=${pageToUse * 20}`;
      
      // Add client filter - this is the key part
      url += `&searchTerm=${encodeURIComponent(selectedClient.client_name)}`;
      
      if (billingFilters.startDate) url += `&start=${billingFilters.startDate}`;
      if (billingFilters.endDate) url += `&end=${billingFilters.endDate}`;
      if (billingFilters.status !== 'ALL') url += `&status=${billingFilters.status}`;

      const res = await API.get(url);
      const data = res.data || [];
      
      // Filter to ensure we only get sessions for this specific client
      const clientSessions = data.filter(session => 
        session.client_name === selectedClient.client_name
      );
      
      const weHaveMore = clientSessions.length > 20;
      const first20 = clientSessions.slice(0, 20);

      if (resetPage) {
        setBillingHistory(first20);
      } else {
        setBillingHistory(prev => [...prev, ...first20]);
      }
      setHasBillingMore(weHaveMore);
    } catch (error) {
      console.error('Error fetching billing history:', error);
    }
  };

  const fetchClientForms = async () => {
    if (!selectedClient) return;
    
    try {
      const res = await API.get(`/forms/admin/intake-data/${selectedClient.id}`);
      setLocalForms(res.data || []);
    } catch (error) {
      console.error('Error fetching client forms:', error);
      setLocalForms([]);
    }
  };

  const fetchClientStats = async () => {
    if (!selectedClient || isLoadingStats) return;
    
    setIsLoadingStats(true);
    try {
      console.log(`Fetching stats for client ID: ${selectedClient.id}, Name: ${selectedClient.client_name}`);
      const res = await API.get(`/admin/clients/${selectedClient.id}/statistics`);
      console.log('Stats response:', res.data);
      setClientStats(res.data);
    } catch (error) {
      console.error('Error fetching client statistics:', error);
      // Set empty stats on error
      setClientStats({
        totalSessions: 0,
        totalBilled: 0,
        totalCollected: 0,
        outstandingBalance: 0,
        paidSessions: 0,
        unpaidSessions: 0,
        debtSessions: 0,
        paymentRate: 0
      });
    } finally {
      setIsLoadingStats(false);
    }
  };

  // Invoice generation
  const generateInvoice = async () => {
    if (!selectedClient) return;
    
    try {
      const startDate = billingFilters.startDate || '2020-01-01';
      const endDate = billingFilters.endDate || todayEastern();
      
      const res = await API.get('/admin/client-invoice', {
        params: {
          clientId: selectedClient.id,
          startDate,
          endDate
        }
      });
      
      setInvoiceData(res.data);
      setShowInvoiceModal(true);
    } catch (error) {
      console.error('Error generating invoice:', error);
      alert('Error generating invoice');
    }
  };

  // Diagnostic function
  const runDiagnostic = async () => {
    if (!selectedClient) return;
    
    try {
      const res = await API.get(`/admin/clients/${selectedClient.id}/diagnostic`);
      setDiagnosticData(res.data);
      setShowDiagnostic(true);
    } catch (error) {
      console.error('Error running diagnostic:', error);
      alert('Error running diagnostic');
    }
  };

  // Removed delete session functionality - this is now view-only

  // Form handling
  const determineFormType = (formData) => {
    const dataJson = formData.data_json || {};
    
    if (dataJson.consentDate && dataJson.clientName) {
      return "Intake Form";
    }
    
    if (dataJson.storedFields && Array.isArray(dataJson.storedFields)) {
      return "Acuity Intake";
    }
    
    if (dataJson.basicInfo) {
      return "Manual Intake";
    }
    
    return "Client Form";
  };

  const handleViewForm = (form) => {
    setSelectedForm(form);
    setShowFormPreview(true);
  };

  // Filtering and display - now includes therapist search from backend data
  const displayedClients = searchTerm
    ? allClients.filter((client) => {
        const therapistName = getClientTherapist(client);
        return client.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
               (client.email && client.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
               (client.phone && client.phone.includes(searchTerm)) ||
               (therapistName && therapistName.toLowerCase().includes(searchTerm.toLowerCase()));
      })
    : allClients;

  // Styles
  const containerStyle = {
    display: 'flex',
    minHeight: 'calc(100vh - 64px)',
    fontFamily: 'Arial, sans-serif',
    flexWrap: 'wrap'
  };

  const sidebarStyle = {
    width: '300px',
    flex: '0 0 300px',
    maxWidth: '100%',
    borderRight: '1px solid #ddd',
    padding: '1rem',
    backgroundColor: '#f8f9fa',
    overflowY: 'auto'
  };

  const mainContentStyle = {
    flex: '1 1 320px',
    padding: '1rem',
    overflowY: 'auto',
    minWidth: 0
  };

  const clientListItemStyle = (isSelected) => ({
    padding: '0.75rem',
    margin: '0.25rem 0',
    borderRadius: '4px',
    cursor: 'pointer',
    backgroundColor: isSelected ? '#1D2C5F' : 'white',
    color: isSelected ? 'white' : 'black',
    border: '1px solid #ddd',
    transition: 'all 0.2s'
  });

  const tabStyle = (isActive) => ({
    padding: '0.75rem 1.5rem',
    backgroundColor: isActive ? '#1D2C5F' : '#f8f9fa',
    color: isActive ? 'white' : 'black',
    border: 'none',
    borderRadius: '4px 4px 0 0',
    cursor: 'pointer',
    marginRight: '0.25rem'
  });

  const cardStyle = {
    backgroundColor: 'white',
    border: '1px solid #ddd',
    borderRadius: '8px',
    padding: '1.5rem',
    marginBottom: '1rem',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
  };

  const modalStyle = {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    background: '#fff',
    border: '1px solid #ccc',
    padding: '2rem',
    borderRadius: '8px',
    boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
    zIndex: 9999,
    width: '90%',
    maxWidth: '800px',
    maxHeight: '90vh',
    overflowY: 'auto'
  };

  const overlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    zIndex: 9998
  };

  // Render functions
  const renderClientOverview = () => {
    const therapistName = getClientTherapist(selectedClient);

    return (
      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3>Client Information</h3>
          <button 
            onClick={startEditingClient}
            style={{
              backgroundColor: '#1D2C5F',
              color: 'white',
              border: 'none',
              padding: '0.5rem 1rem',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Edit Client
          </button>
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <div>
            <p><strong>Name:</strong> {selectedClient.client_name}</p>
            <p><strong>Primary Therapist:</strong> {therapistName || 'Not assigned'}</p>
            <p><strong>Phone:</strong> {selectedClient.phone || 'Not provided'}</p>
            <p><strong>Email:</strong> {selectedClient.email || 'Not provided'}</p>
            <p><strong>Emergency Contact:</strong> {selectedClient.emergency_contact_name || 'Not provided'}</p>
            <p><strong>Emergency Phone:</strong> {selectedClient.emergency_contact_phone || 'Not provided'}</p>
          </div>
          <div>
            <p><strong>Billing Address:</strong> {selectedClient.billing_address || 'Not provided'}</p>
            <p><strong>Billing Email:</strong> {selectedClient.billing_email || 'Not provided'}</p>
            <p><strong>Diagnosis Code:</strong> {selectedClient.diagnosis_code || 'Not provided'}</p>
            <p><strong>Is Minor:</strong> {selectedClient.is_minor ? 'Yes' : 'No'}</p>
            {selectedClient.is_minor && (
              <>
                <p><strong>Guardian Name:</strong> {selectedClient.guardian_name || 'Not provided'}</p>
                <p><strong>Guardian Phone:</strong> {selectedClient.guardian_phone || 'Not provided'}</p>
              </>
            )}
          </div>
        </div>
        
        {/* Client Statistics */}
        <div style={{ marginTop: '2rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h4>Client Statistics</h4>
            <button
              onClick={runDiagnostic}
              style={{
                backgroundColor: '#ffc107',
                color: 'black',
                border: 'none',
                padding: '0.5rem 1rem',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.9rem'
              }}
            >
              Debug Billing
            </button>
          </div>
          {isLoadingStats ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>
              Loading statistics...
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1rem', textAlign: 'center' }}>
              <div>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1D2C5F' }}>
                  {clientStats.totalSessions || 0}
                </div>
                <div>Total Sessions</div>
              </div>
              <div>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#28a745' }}>
                  {formatCurrency(clientStats.totalBilled || 0)}
                </div>
                <div>Total Billed</div>
              </div>
              <div>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#ffc107' }}>
                  {formatCurrency(clientStats.totalCollected || 0)}
                </div>
                <div>Total Collected</div>
              </div>
              <div>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: (clientStats.outstandingBalance || 0) > 0 ? '#dc3545' : '#28a745' }}>
                  {formatCurrency(clientStats.outstandingBalance || 0)}
                </div>
                <div>Outstanding Balance</div>
              </div>
            </div>
          )}
          
          {/* Additional stats row */}
          {!isLoadingStats && clientStats && Object.keys(clientStats).length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1rem', textAlign: 'center', marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #dee2e6' }}>
              <div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#17a2b8' }}>
                  {clientStats.paidSessions || 0}
                </div>
                <div>Paid Sessions</div>
              </div>
              <div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#dc3545' }}>
                  {clientStats.unpaidSessions || 0}
                </div>
                <div>Unpaid Sessions</div>
              </div>
              <div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#fd7e14' }}>
                  {clientStats.debtSessions || 0}
                </div>
                <div>Debt Sessions</div>
              </div>
              <div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#6c757d' }}>
                  {clientStats.paymentRate ? `${clientStats.paymentRate}%` : '0%'}
                </div>
                <div>Payment Rate</div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderBillingHistory = () => (
    <div style={cardStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h3>Billing History</h3>
        <button 
          onClick={generateInvoice}
          style={{
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            padding: '0.5rem 1rem',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Generate Invoice
        </button>
      </div>
      
      {/* Filters */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', alignItems: 'center' }}>
        <div>
          <label>Start Date:</label>
          <input
            type="date"
            value={billingFilters.startDate}
            onChange={(e) => setBillingFilters(prev => ({ ...prev, startDate: e.target.value }))}
            style={{ marginLeft: '0.5rem', padding: '0.25rem' }}
          />
        </div>
        <div>
          <label>End Date:</label>
          <input
            type="date"
            value={billingFilters.endDate}
            onChange={(e) => setBillingFilters(prev => ({ ...prev, endDate: e.target.value }))}
            style={{ marginLeft: '0.5rem', padding: '0.25rem' }}
          />
        </div>
        <div>
          <label>Status:</label>
          <select
            value={billingFilters.status}
            onChange={(e) => setBillingFilters(prev => ({ ...prev, status: e.target.value }))}
            style={{ marginLeft: '0.5rem', padding: '0.25rem' }}
          >
            <option value="ALL">All</option>
            <option value="PAID">Paid</option>
            <option value="UNPAID">Unpaid</option>
            <option value="DEBT">Debt</option>
            <option value="SUBMITTED">Submitted</option>
          </select>
        </div>
        <button 
          onClick={() => fetchClientBillingHistory(true)}
          style={{
            backgroundColor: '#1D2C5F',
            color: 'white',
            border: 'none',
            padding: '0.5rem 1rem',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Filter
        </button>
      </div>
      
      {/* Billing Table */}
      {billingHistory.length === 0 ? (
        <p>No billing history found for this client.</p>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <th style={{ padding: '0.75rem', border: '1px solid #ddd', textAlign: 'left' }}>Date</th>
                  <th style={{ padding: '0.75rem', border: '1px solid #ddd', textAlign: 'left' }}>Therapist</th>
                  <th style={{ padding: '0.75rem', border: '1px solid #ddd', textAlign: 'left' }}>Service Type</th>
                  <th style={{ padding: '0.75rem', border: '1px solid #ddd', textAlign: 'left' }}>Billed</th>
                  <th style={{ padding: '0.75rem', border: '1px solid #ddd', textAlign: 'left' }}>Collected</th>
                  <th style={{ padding: '0.75rem', border: '1px solid #ddd', textAlign: 'left' }}>Payment Type</th>
                  <th style={{ padding: '0.75rem', border: '1px solid #ddd', textAlign: 'left' }}>Status</th>
                  <th style={{ padding: '0.75rem', border: '1px solid #ddd', textAlign: 'left' }}>Notes</th>
                </tr>
              </thead>
              <tbody>
                {billingHistory.map((session) => (
                  <tr key={session.id}>
                    <td style={{ padding: '0.75rem', border: '1px solid #ddd' }}>
                      {formatSessionDate(session.session_date)}
                    </td>
                    <td style={{ padding: '0.75rem', border: '1px solid #ddd' }}>
                      {session.therapist_first_name} {session.therapist_last_name}
                    </td>
                    <td style={{ padding: '0.75rem', border: '1px solid #ddd' }}>
                      {session.service_type || 'Session'}
                    </td>
                    <td style={{ padding: '0.75rem', border: '1px solid #ddd' }}>
                      {formatCurrency(session.billed_amount)}
                    </td>
                    <td style={{ padding: '0.75rem', border: '1px solid #ddd' }}>
                      {formatCurrency(session.collected_amount)}
                    </td>
                    <td style={{ padding: '0.75rem', border: '1px solid #ddd' }}>
                      {session.payment_type && (
                        <span style={getPaymentStyle(session.payment_type)}>
                          {session.payment_type}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '0.75rem', border: '1px solid #ddd' }}>
                      <span style={getStatusBadge(session.status)}>
                        {session.status}
                      </span>
                    </td>
                    <td style={{ padding: '0.75rem', border: '1px solid #ddd' }}>
                      {session.notes || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {hasBillingMore && (
            <button 
              onClick={() => {
                setBillingPage(prev => prev + 1);
                fetchClientBillingHistory(false);
              }}
              style={{
                marginTop: '1rem',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                padding: '0.5rem 1rem',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Load More
            </button>
          )}
        </>
      )}
    </div>
  );

  const renderClientForms = () => (
    <div style={cardStyle}>
      <h3>Client Forms</h3>
      {localForms.length === 0 ? (
        <p>No forms found for this client</p>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
          {localForms.map((form) => {
            const formType = determineFormType(form);
            const formDate = formatSessionDate(form.created_at);
            
            return (
              <button
                key={form.id}
                onClick={() => handleViewForm(form)}
                style={{
                  padding: '0.75rem 1rem',
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #dee2e6',
                  borderRadius: '4px',
                  color: '#212529',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = '#e9ecef';
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = '#f8f9fa';
                }}
              >
                {formType} - {formDate}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  const renderClientEditor = () => (
    <>
      <div style={overlayStyle} onClick={() => setShowClientEditor(false)} />
      <div style={modalStyle}>
        <h3>Edit Client Information</h3>
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
          <div>
            <label>Client Name *</label>
            <input
              type="text"
              value={editedClientData.clientName}
              onChange={(e) => setEditedClientData(prev => ({ ...prev, clientName: e.target.value }))}
              style={{ width: '100%', padding: '0.5rem', marginTop: '0.25rem' }}
              required
            />
          </div>
          
          <div>
            <label>Phone</label>
            <input
              type="tel"
              value={editedClientData.phone}
              onChange={(e) => setEditedClientData(prev => ({ ...prev, phone: e.target.value }))}
              style={{ width: '100%', padding: '0.5rem', marginTop: '0.25rem' }}
            />
          </div>
          
          <div>
            <label>Email</label>
            <input
              type="email"
              value={editedClientData.email}
              onChange={(e) => setEditedClientData(prev => ({ ...prev, email: e.target.value }))}
              style={{ width: '100%', padding: '0.5rem', marginTop: '0.25rem' }}
            />
          </div>
          
          <div>
            <label>Emergency Contact Name</label>
            <input
              type="text"
              value={editedClientData.emergencyContactName}
              onChange={(e) => setEditedClientData(prev => ({ ...prev, emergencyContactName: e.target.value }))}
              style={{ width: '100%', padding: '0.5rem', marginTop: '0.25rem' }}
            />
          </div>
          
          <div>
            <label>Emergency Contact Phone</label>
            <input
              type="tel"
              value={editedClientData.emergencyContactPhone}
              onChange={(e) => setEditedClientData(prev => ({ ...prev, emergencyContactPhone: e.target.value }))}
              style={{ width: '100%', padding: '0.5rem', marginTop: '0.25rem' }}
            />
          </div>
          
          <div>
            <label>Diagnosis Code</label>
            <input
              type="text"
              value={editedClientData.diagnosisCode}
              onChange={(e) => setEditedClientData(prev => ({ ...prev, diagnosisCode: e.target.value }))}
              style={{ width: '100%', padding: '0.5rem', marginTop: '0.25rem' }}
            />
          </div>
          
          <div>
            <label>Billing Email</label>
            <input
              type="email"
              value={editedClientData.billingEmail}
              onChange={(e) => setEditedClientData(prev => ({ ...prev, billingEmail: e.target.value }))}
              style={{ width: '100%', padding: '0.5rem', marginTop: '0.25rem' }}
            />
          </div>
          
          <div>
            <label>Billing Address</label>
            <input
              type="text"
              value={editedClientData.billingAddress}
              onChange={(e) => setEditedClientData(prev => ({ ...prev, billingAddress: e.target.value }))}
              style={{ width: '100%', padding: '0.5rem', marginTop: '0.25rem' }}
            />
          </div>
        </div>
        
        <div style={{ marginBottom: '1rem' }}>
          <label>
            <input
              type="checkbox"
              checked={editedClientData.isMinor}
              onChange={(e) => setEditedClientData(prev => ({ ...prev, isMinor: e.target.checked }))}
              style={{ marginRight: '0.5rem' }}
            />
            Is Minor
          </label>
        </div>
        
        {editedClientData.isMinor && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label>Guardian Name</label>
              <input
                type="text"
                value={editedClientData.guardianName}
                onChange={(e) => setEditedClientData(prev => ({ ...prev, guardianName: e.target.value }))}
                style={{ width: '100%', padding: '0.5rem', marginTop: '0.25rem' }}
              />
            </div>
            
            <div>
              <label>Guardian Phone</label>
              <input
                type="tel"
                value={editedClientData.guardianPhone}
                onChange={(e) => setEditedClientData(prev => ({ ...prev, guardianPhone: e.target.value }))}
                style={{ width: '100%', padding: '0.5rem', marginTop: '0.25rem' }}
              />
            </div>
          </div>
        )}
        
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
          <button 
            onClick={() => setShowClientEditor(false)}
            style={{
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button 
            onClick={saveClientEdits}
            style={{
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Save Changes
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div style={containerStyle}>
      {/* Sidebar - Client Search */}
      <div style={sidebarStyle}>
        <h3>Client Search</h3>
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by name, email, phone, or therapist..."
          style={{
            width: '100%',
            padding: '0.75rem',
            marginBottom: '1rem',
            border: '1px solid #ddd',
            borderRadius: '4px'
          }}
        />
        
        <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {displayedClients.length === 0 ? (
            <div style={{ color: '#6c757d', textAlign: 'center', padding: '1rem' }}>
              No clients found
            </div>
          ) : (
            displayedClients.map((client) => {
              const therapistName = getClientTherapist(client);
              
              return (
                <div
                  key={client.id}
                  style={clientListItemStyle(selectedClient && selectedClient.id === client.id)}
                  onClick={() => handleSelectClient(client)}
                >
                  <div style={{ fontWeight: 'bold' }}>
                    {client.client_name}
                    {therapistName && (
                      <span style={{ fontWeight: 'normal', opacity: 0.8 }}>
                        {' '}({therapistName})
                      </span>
                    )}
                  </div>
                  {client.email && <div style={{ fontSize: '0.85rem', opacity: 0.8 }}>{client.email}</div>}
                  {client.phone && <div style={{ fontSize: '0.85rem', opacity: 0.8 }}>{client.phone}</div>}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Main Content */}
      <div style={mainContentStyle}>
        {!selectedClient ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>
            <h3>Select a client to view their profile</h3>
            <p>Use the search bar on the left to find and select a client.</p>
          </div>
        ) : (
          <>
            {/* Header */}
            <div style={{ marginBottom: '2rem' }}>
              <h2>
                {selectedClient.client_name}
                {(() => {
                  const therapistName = getClientTherapist(selectedClient);
                  return therapistName ? ` (${therapistName})` : '';
                })()}
              </h2>
              <div style={{ color: '#6c757d' }}>
                Client ID: {selectedClient.id} | 
                {selectedClient.email && ` Email: ${selectedClient.email} |`}
                {selectedClient.phone && ` Phone: ${selectedClient.phone}`}
              </div>
            </div>

            {/* Tabs */}
            <div style={{ marginBottom: '1rem' }}>
              <button 
                style={tabStyle(activeTab === 'overview')}
                onClick={() => setActiveTab('overview')}
              >
                Overview
              </button>
              <button 
                style={tabStyle(activeTab === 'billing')}
                onClick={() => setActiveTab('billing')}
              >
                Billing History
              </button>
              <button 
                style={tabStyle(activeTab === 'forms')}
                onClick={() => setActiveTab('forms')}
              >
                Forms
              </button>
            </div>

            {/* Tab Content */}
            {activeTab === 'overview' && renderClientOverview()}
            {activeTab === 'billing' && renderBillingHistory()}
            {activeTab === 'forms' && renderClientForms()}
          </>
        )}
      </div>

      {/* Modals */}
      {showClientEditor && renderClientEditor()}
      
      {/* Diagnostic Modal */}
      {showDiagnostic && diagnosticData && (
        <>
          <div style={overlayStyle} onClick={() => setShowDiagnostic(false)} />
          <div style={{...modalStyle, width: '95%', maxWidth: '1200px'}}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3>Billing Analysis for {selectedClient.client_name}</h3>
              <button 
                onClick={() => setShowDiagnostic(false)}
                style={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  padding: '0.5rem 1rem',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
            
            {/* Summary */}
            <div style={{ backgroundColor: '#f8f9fa', padding: '1rem', borderRadius: '4px', marginBottom: '1rem' }}>
              <h4>Summary</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div><strong>Total Sessions:</strong> {diagnosticData.summary?.totalSessions || 0}</div>
                <div><strong>Total Billed:</strong> {formatCurrency(diagnosticData.summary?.totalBilled || 0)}</div>
                <div><strong>Total Collected:</strong> {formatCurrency(diagnosticData.summary?.totalCollected || 0)}</div>
                <div><strong>Outstanding:</strong> {formatCurrency(diagnosticData.summary?.outstandingBalance || 0)}</div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1rem' }}>
                <div><strong>Paid Sessions:</strong> {diagnosticData.summary?.sessionsByStatus?.PAID || 0}</div>
                <div><strong>Unpaid Sessions:</strong> {diagnosticData.summary?.sessionsByStatus?.UNPAID || 0}</div>
                <div><strong>Debt Sessions:</strong> {diagnosticData.summary?.sessionsByStatus?.DEBT || 0}</div>
                <div><strong>Payment Rate:</strong> {diagnosticData.summary?.paymentRate || 0}%</div>
              </div>
            </div>
            
            {/* Billing Issues */}
            {diagnosticData.billingIssues && diagnosticData.billingIssues.length > 0 && (
              <div style={{ marginBottom: '1rem' }}>
                <h4 style={{ color: '#dc3545' }}>Billing Issues Found ({diagnosticData.billingIssues.length})</h4>
                <div style={{ overflowX: 'auto', maxHeight: '200px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#dc3545', color: 'white' }}>
                        <th style={{ padding: '0.5rem', border: '1px solid #ddd' }}>Date</th>
                        <th style={{ padding: '0.5rem', border: '1px solid #ddd' }}>Issue Type</th>
                        <th style={{ padding: '0.5rem', border: '1px solid #ddd' }}>Status</th>
                        <th style={{ padding: '0.5rem', border: '1px solid #ddd' }}>Billed</th>
                        <th style={{ padding: '0.5rem', border: '1px solid #ddd' }}>Collected</th>
                        <th style={{ padding: '0.5rem', border: '1px solid #ddd' }}>Description</th>
                        <th style={{ padding: '0.5rem', border: '1px solid #ddd' }}>Therapist</th>
                      </tr>
                    </thead>
                    <tbody>
                      {diagnosticData.billingIssues.map((issue, idx) => (
                        <tr key={idx}>
                          <td style={{ padding: '0.5rem', border: '1px solid #ddd' }}>
                            {formatSessionDate(issue.date)}
                          </td>
                          <td style={{ padding: '0.5rem', border: '1px solid #ddd', fontWeight: 'bold' }}>
                            {issue.issueType}
                          </td>
                          <td style={{ padding: '0.5rem', border: '1px solid #ddd' }}>{issue.status}</td>
                          <td style={{ padding: '0.5rem', border: '1px solid #ddd' }}>
                            {formatCurrency(issue.billed)}
                          </td>
                          <td style={{ padding: '0.5rem', border: '1px solid #ddd' }}>
                            {formatCurrency(issue.collected)}
                          </td>
                          <td style={{ padding: '0.5rem', border: '1px solid #ddd' }}>{issue.description}</td>
                          <td style={{ padding: '0.5rem', border: '1px solid #ddd' }}>{issue.therapist}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            
            {/* Potential Duplicates */}
            {diagnosticData.potentialDuplicates && diagnosticData.potentialDuplicates.length > 0 && (
              <div style={{ marginBottom: '1rem' }}>
                <h4 style={{ color: '#ffc107' }}>Potential Duplicate Sessions ({diagnosticData.potentialDuplicates.length})</h4>
                <div style={{ overflowX: 'auto', maxHeight: '200px' }}>
                  {diagnosticData.potentialDuplicates.map((duplicate, idx) => (
                    <div key={idx} style={{ 
                      backgroundColor: '#fff3cd', 
                      border: '1px solid #ffeaa7', 
                      padding: '0.75rem', 
                      marginBottom: '0.5rem',
                      borderRadius: '4px'
                    }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
                        Date: {duplicate.date} - {duplicate.reason}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                        {duplicate.sessions.map((session, sessionIdx) => (
                          <div key={sessionIdx} style={{ 
                            backgroundColor: 'white', 
                            padding: '0.5rem', 
                            borderRadius: '4px',
                            border: '1px solid #ddd'
                          }}>
                            <div><strong>Session ID:</strong> {session.id}</div>
                            <div><strong>Billed:</strong> {formatCurrency(session.billed)}</div>
                            <div><strong>Collected:</strong> {formatCurrency(session.collected)}</div>
                            <div><strong>Status:</strong> {session.status}</div>
                            <div><strong>Therapist:</strong> {session.therapist}</div>
                            <div style={{ fontSize: '0.8rem', color: '#6c757d' }}>
                              Created: {new Date(session.createdAt).toLocaleString()}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* All Sessions */}
            <div>
              <h4>All Sessions ({diagnosticData.allSessions?.length || 0})</h4>
              <div style={{ overflowX: 'auto', maxHeight: '400px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                      <th style={{ padding: '0.5rem', border: '1px solid #ddd' }}>Date</th>
                      <th style={{ padding: '0.5rem', border: '1px solid #ddd' }}>Status</th>
                      <th style={{ padding: '0.5rem', border: '1px solid #ddd' }}>Service</th>
                      <th style={{ padding: '0.5rem', border: '1px solid #ddd' }}>Billed</th>
                      <th style={{ padding: '0.5rem', border: '1px solid #ddd' }}>Collected</th>
                      <th style={{ padding: '0.5rem', border: '1px solid #ddd' }}>Difference</th>
                      <th style={{ padding: '0.5rem', border: '1px solid #ddd' }}>Payment Type</th>
                      <th style={{ padding: '0.5rem', border: '1px solid #ddd' }}>Therapist</th>
                      <th style={{ padding: '0.5rem', border: '1px solid #ddd' }}>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {diagnosticData.allSessions && diagnosticData.allSessions.map((session, idx) => (
                      <tr key={idx} style={{ 
                        backgroundColor: Math.abs(session.difference) > 0.01 ? '#ffebee' : 'white' 
                      }}>
                        <td style={{ padding: '0.5rem', border: '1px solid #ddd' }}>
                          {formatSessionDate(session.date)}
                        </td>
                        <td style={{ padding: '0.5rem', border: '1px solid #ddd' }}>
                          <span style={getStatusBadge(session.status)}>
                            {session.status}
                          </span>
                        </td>
                        <td style={{ padding: '0.5rem', border: '1px solid #ddd' }}>{session.serviceType}</td>
                        <td style={{ padding: '0.5rem', border: '1px solid #ddd' }}>
                          {formatCurrency(session.billed)}
                        </td>
                        <td style={{ padding: '0.5rem', border: '1px solid #ddd' }}>
                          {formatCurrency(session.collected)}
                        </td>
                        <td style={{ 
                          padding: '0.5rem', 
                          border: '1px solid #ddd',
                          color: Math.abs(session.difference) > 0.01 ? '#dc3545' : '#28a745',
                          fontWeight: Math.abs(session.difference) > 0.01 ? 'bold' : 'normal'
                        }}>
                          {formatCurrency(session.difference)}
                        </td>
                        <td style={{ padding: '0.5rem', border: '1px solid #ddd' }}>
                          {session.paymentType && (
                            <span style={getPaymentStyle(session.paymentType)}>
                              {session.paymentType}
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '0.5rem', border: '1px solid #ddd' }}>{session.therapist}</td>
                        <td style={{ padding: '0.5rem', border: '1px solid #ddd', maxWidth: '200px', wordBreak: 'break-word' }}>
                          {session.notes}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            
            <div style={{ marginTop: '1rem', padding: '0.5rem', backgroundColor: '#e9ecef', borderRadius: '4px', fontSize: '0.9rem', color: '#6c757d' }}>
              Analysis generated on: {diagnosticData.analysisDate ? new Date(diagnosticData.analysisDate).toLocaleString() : 'Unknown'}
            </div>
          </div>
        </>
      )}
      
      {/* Add other modals for forms, invoices, etc. as needed */}
    </div>
  );
}

export default AdminClientProfilePage;